import { useState } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { MedicusLogo } from '@components/MedicusLogo';
import { login as loginApi } from '@lib/services/auth.service';
import { useAuthStore } from '@lib/store/auth.store';
import { UserRole, esTelemarketer } from '@lib/types';

/** TMK no tiene acceso al dashboard, lo mandamos directo al buscador. */
function rutaPorRol(rol: UserRole): string {
  return esTelemarketer(rol) ? '/busqueda' : '/dashboard';
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession, token, user } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Si ya hay sesión, mandamos a la home según el rol.
  if (token && user) {
    return <Navigate to={rutaPorRol(user.rol)} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { token: jwt, user: u, refreshToken } = await loginApi(email, password);
      setSession(jwt, u, refreshToken);
      const from = (location.state as { from?: string } | null)?.from ?? rutaPorRol(u.rol);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(e.response?.data?.error?.message ?? 'Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="loginShell">
      <div className="loginCard">
        <div className="loginCard__brand">
          <MedicusLogo width={140} />
        </div>
        <div className="card-elevated">
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h1 className="heading-3">Iniciá sesión</h1>
          </div>

          <form onSubmit={handleSubmit}>
            <label className="formField">
              <span>Usuario</span>
              <input
                className="input"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="username"
              />
            </label>

            <label className="formField" style={{ marginTop: '0.75rem' }}>
              <span>Contraseña</span>
              <input
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </label>

            {error && (
              <p className="status status-error" style={{ marginTop: '1rem', display: 'block' }}>
                {error}
              </p>
            )}

            <button
              className="btn btn-primary"
              type="submit"
              disabled={loading}
              style={{ marginTop: '1.5rem', width: '100%' }}
            >
              <span className="btn-font-style">
                {loading ? 'Ingresando…' : 'Ingresar'}
              </span>
            </button>
          </form>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 16,
            fontSize: 12,
            color: '#94a3b8',
          }}
        >
          <span>© Medicus S.A.</span>
          <Link
            to="/"
            style={{
              color: '#94a3b8',
              textDecoration: 'none',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color = 'var(--color-primary-darker)')
            }
            onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
          >
            <ArrowLeft size={14} strokeWidth={2.5} />
            Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}
